import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, map, merge, Observable, of, skip, startWith, switchMap, tap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { FreigabeEntscheidungComponent } from '../freigabe-entscheidung/freigabe-entscheidung.component';
import { ReleaseService } from '../../../services/release.service';
import { ReleaseAssessment, ReleaseCriterion, ReleasePhase } from '../../../models/release-assessment.model';
import { TEST_CASE_PRIORITIES, TestCasePriority } from '../../../models/test-case.model';
import { formatIsoDateDe } from '../../../shared/date.util';

interface AssessmentState {
  loading: boolean;
  error: boolean;
  data: ReleaseAssessment | null;
}

interface AssessmentRequest {
  projectId: string;
  releaseId: string;
}

type PhaseState = 'done' | 'aktuell' | 'offen';

const PRIORITIES_DESC: readonly TestCasePriority[] = [...TEST_CASE_PRIORITIES].reverse();

@Component({
  selector: 'app-freigabe-bewertung',
  standalone: true,
  imports: [TranslatePipe, FreigabeEntscheidungComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './freigabe-bewertung.component.html',
  styleUrl: './freigabe-bewertung.component.scss',
})
export class FreigabeBewertungComponent {
  private readonly releaseService = inject(ReleaseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entscheidungsBereich = viewChild(FreigabeEntscheidungComponent);

  readonly projectId = input.required<string>();
  readonly releaseId = input.required<string>();
  /** Wechselt nach einem Import; die Bewertung wird dann neu geholt, ohne ungespeicherte Eingaben zu verlieren. */
  readonly aktualisierung = input(0);
  readonly teststufe = input<string | null>(null);

  readonly decisionSaved = output<ReleaseAssessment>();
  readonly protokollExportieren = output<void>();

  protected readonly priorities = PRIORITIES_DESC;
  protected readonly state = signal<AssessmentState>({ loading: false, error: false, data: null });
  protected readonly aktualisierungFehler = signal(false);

  protected readonly assessment = computed(() => this.state().data);

  hasUnsavedChanges(): boolean {
    return this.entscheidungsBereich()?.hasUnsavedChanges() ?? false;
  }

  private readonly request = computed<AssessmentRequest>(() => ({
    projectId: this.projectId(),
    releaseId: this.releaseId(),
  }));

  constructor() {
    merge(
      toObservable(this.request).pipe(map((req) => this.laden(req))),
      toObservable(this.aktualisierung).pipe(
        skip(1),
        map(() => this.neuLaden()),
      ),
    )
      .pipe(
        switchMap((abruf) => abruf),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected onAssessmentChange(data: ReleaseAssessment): void {
    this.state.set({ loading: false, error: false, data });
    this.decisionSaved.emit(data);
  }

  protected phaseState(phase: ReleasePhase): PhaseState {
    if (phase.done) return 'done';
    return phase.aktuell ? 'aktuell' : 'offen';
  }

  protected formatDate(iso: string | null): string {
    return formatIsoDateDe(iso);
  }

  protected gateTone(criterion: ReleaseCriterion): 'ok' | 'muss' | 'soll' {
    if (criterion.ok) return 'ok';
    return criterion.muss ? 'muss' : 'soll';
  }

  protected percent(aus: number, ges: number): number {
    return ges > 0 ? Math.round((aus / ges) * 100) : 0;
  }

  protected stripes(count: number): number[] {
    return Array.from({ length: Math.max(count, 0) }, (_, i) => i);
  }

  private laden(req: AssessmentRequest): Observable<AssessmentState> {
    this.aktualisierungFehler.set(false);
    return this.releaseService.getAssessment(req.projectId, req.releaseId).pipe(
      map((data): AssessmentState => ({ loading: false, error: false, data })),
      catchError(() => of<AssessmentState>({ loading: false, error: true, data: null })),
      startWith<AssessmentState>({ loading: true, error: false, data: null }),
      tap((state) => this.state.set(state)),
    );
  }

  private neuLaden(): Observable<ReleaseAssessment> {
    const req = this.request();
    return this.releaseService.getAssessment(req.projectId, req.releaseId).pipe(
      tap((data) => {
        this.state.set({ loading: false, error: false, data });
        this.aktualisierungFehler.set(false);
      }),
      catchError(() => {
        if (this.state().data) {
          this.aktualisierungFehler.set(true);
        } else {
          this.state.set({ loading: false, error: true, data: null });
        }
        return EMPTY;
      }),
    );
  }
}
